import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail, setStoredAccessToken, clearStoredAccessToken, postWithRetry } from "../lib/api";

const AuthContext = createContext(null);

/**
 * Splits the auth response into (userObject, accessToken).
 * Backend returns the user fields plus an optional `access_token` field for
 * the Bearer-fallback. We strip the token before exposing the user object.
 */
function splitAuthResponse(data) {
  if (!data || typeof data !== "object") return { user: data, token: null };
  const { access_token, token_type, ...user } = data; // eslint-disable-line no-unused-vars
  return { user, token: access_token || null };
}

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = authenticated
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (_e) {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    (async () => {
      await refreshMe();
      setLoading(false);
    })();
  }, [refreshMe]);

  const login = async (email, password, { onRetry } = {}) => {
    try {
      // Iter 28 — retry on network / 5xx (cold start), not on 401.
      const { data } = await postWithRetry("/auth/login", { email, password }, { retries: 2, onRetry });
      const { user: u, token } = splitAuthResponse(data);
      if (token) setStoredAccessToken(token);
      setUser(u);
      return { ok: true, user: u };
    } catch (e) {
      // No response from server at all → friendly network message
      if (!e.response) {
        return { ok: false, error: "Connection issue. Please check your internet and try again.", kind: "network" };
      }
      // 5xx after retries exhausted
      if (e.response.status >= 500) {
        return { ok: false, error: "The server is taking longer than expected. Please try again.", kind: "server" };
      }
      // 401 / 400 / other 4xx — real error from backend
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message, kind: "auth" };
    }
  };

  const register = async (email, password, name, opts = {}) => {
    // iter130 Phase A — additional signup fields: phone (optional) +
    // DPDP consent (required). `onRetry` still supported for cold-start
    // retry UX. Legacy callers passing only email/password/name keep
    // working — consent defaults to false and backend returns 400 so
    // the frontend UI must show + wire the checkbox.
    const { onRetry, phone, consent } = opts;
    try {
      const body = { email, password, name };
      if (phone) body.phone = phone;
      if (consent !== undefined) body.consent = consent;
      const { data } = await postWithRetry("/auth/register", body, { retries: 2, onRetry });
      const { user: u, token } = splitAuthResponse(data);
      if (token) setStoredAccessToken(token);
      setUser(u);
      return { ok: true, user: u };
    } catch (e) {
      if (!e.response) {
        return { ok: false, error: "Connection issue. Please check your internet and try again.", kind: "network" };
      }
      if (e.response.status >= 500) {
        return { ok: false, error: "The server is taking longer than expected. Please try again.", kind: "server" };
      }
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message, kind: "auth" };
    }
  };

  // iter130 Phase A — Google Sign-In via Google Identity Services on
  // the client. Frontend calls this after receiving an ID token from
  // the GIS button. Backend verifies signature + attaches to existing
  // email account or creates a new one.
  const signInWithGoogle = async (idToken, { name, phone, consent } = {}) => {
    try {
      const { data } = await api.post("/auth/google", {
        id_token: idToken,
        name, phone, consent,
      });
      const { user: u, token } = splitAuthResponse(data);
      if (token) setStoredAccessToken(token);
      setUser(u);
      return { ok: true, user: u };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  // iter130 Phase A — Magic-link login: request → user gets email →
  // clicks link → lands on /auth/magic?t=... which calls consume.
  const requestMagicLink = async (email) => {
    try {
      await api.post("/auth/magic-link/request", { email });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const consumeMagicLink = async (token) => {
    try {
      const { data } = await api.post("/auth/magic-link/consume", { token });
      const { user: u, token: jwt } = splitAuthResponse(data);
      if (jwt) setStoredAccessToken(jwt);
      setUser(u);
      return { ok: true, user: u };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  // iter130 Phase A — Forgot password. Method="email" | "phone".
  const requestPasswordReset = async ({ method, email, phone }) => {
    try {
      await api.post("/auth/forgot", { method, email, phone });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const confirmPasswordReset = async ({ token, email, otp, newPassword }) => {
    try {
      await api.post("/auth/reset/confirm", {
        token, email, otp, new_password: newPassword,
      });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Server-side cookie clear failed — proceed with client-side logout anyway
      if (process.env.NODE_ENV !== "production") {
        console.debug("[AuthContext] logout request failed:", e.message);
      }
    }
    clearStoredAccessToken();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout, refreshMe,
      signInWithGoogle, requestMagicLink, consumeMagicLink,
      requestPasswordReset, confirmPasswordReset,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
